'use client';

/**
 * ACADEMIE NDIAMSYL PEYE — les 4 niveaux :
 *  Foriji (alphabet/makharij) · Digdaum (tajwid) · Hifz (mémorisation) · Kang (tafsir)
 * Exercices « Écouter & Répéter » via le moteur vocal, progression localStorage.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import PageBanner from './PageBanner';
import { LEVEL_INTRO, FORIJI_MODULES, MAKHARIJ_ZONES, DIGDAUM_RULES, HIFZ_SURAHS, HIFZ_TECHNIQUES, HIFZ_ROOMS, KANG_ULOOM, KANG_MUFASSIRUN, KANG_TAFSIR_FATIHA } from '@/data/academy';
import { speakQueue, speakRepeat, speakText, stopSpeech } from '@/lib/speech';
import { GraduationCap, ChevronDown, ChevronUp, Volume2, Ear, BookMarked, Brain, Crown, Award, Flame, CheckCircle2, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';

type LevelKey = 'foriji' | 'digdaum' | 'hifz' | 'kang';

const LEVELS: { key: LevelKey; fr: string; en: string; ar: string; sub: string; icon: typeof Ear }[] = [
  { key: 'foriji', fr: 'Foriji', en: 'Foriji', ar: 'الفُرْجِي', sub: 'Alphabet & Nourania', icon: Ear },
  { key: 'digdaum', fr: 'Digdaum', en: 'Digdaum', ar: 'الدِّدْغَام', sub: 'Tajwid', icon: Volume2 },
  { key: 'hifz', fr: 'Hifz', en: 'Hifz', ar: 'حِفْظ', sub: 'Mémorisation', icon: Brain },
  { key: 'kang', fr: 'Kang', en: 'Kang', ar: 'كَانْغ', sub: 'Tafsir & Sciences', icon: Crown },
];

const PROG_KEY = 'q1000_academy';

interface AcademyProgress { [lessonKey: string]: boolean }

function loadProgress(): AcademyProgress {
  if (typeof window === 'undefined') return {};
  try { return JSON.parse(localStorage.getItem(PROG_KEY) || '{}'); } catch { return {}; }
}

export default function AcademySection() {
  const { uiLang, academyLevel } = useApp();
  const t = DICTS[uiLang];
  const [level, setLevel] = useState<LevelKey>('foriji');
  const [progress, setProgress] = useState<AcademyProgress>({});
  const [openModule, setOpenModule] = useState<string | null>('harakat');
  const [openRule, setOpenRule] = useState<number | null>(0);
  const [hifzSurah, setHifzSurah] = useState(1);
  const [room, setRoom] = useState(0);
  const [memorized, setMemorized] = useState<Record<string, boolean>>({});
  const [kangUloom, setKangUloom] = useState('tafsir');
  const [langSelect, setLangSelect] = useState<'fr' | 'en' | 'ar'>('fr');
  const [examMode, setExamMode] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [lastHubLevel, setLastHubLevel] = useState(academyLevel);

  // Hydratation locale (ajustement d'état pendant le rendu — conforme aux règles React)
  if (!hydrated) {
    setHydrated(true);
    setProgress(loadProgress());
  }

  // Niveau pré-sélectionné depuis le hub ou le menu Académie (Foriji/Digdaum/Hifz/Kang)
  if (academyLevel !== lastHubLevel) {
    setLastHubLevel(academyLevel);
    if (academyLevel) setLevel(academyLevel);
  }

  const toggleDone = (key: string) => {
    const next = { ...progress, [key]: !progress[key] };
    setProgress(next);
    try { localStorage.setItem(PROG_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const levelProgress = (lv: LevelKey) => {
    const keys = Object.keys(progress).filter((k) => k.startsWith(lv));
    if (!keys.length) return 0;
    return Math.round((keys.filter((k) => progress[k]).length / keys.length) * 100);
  };

  const intro = LEVEL_INTRO[level];
  const introText = intro[uiLang === 'ar' ? 'ar' : uiLang === 'en' ? 'en' : 'fr'];

  const lbl = (fr: string, en: string, ar: string) => (uiLang === 'ar' ? ar : uiLang === 'en' ? en : fr);

  /* ============ SOURATES HIFZ : texte arabe ============ */
  const hifzAyahs = useMemo(() => {
    // petites sourates incluses dans quran_full.json chargé à la demande
    return null; // rempli par le fetch ci-dessous
  }, []);
  const [hifzText, setHifzText] = useState<string[]>([]);
  useEffect(() => {
    fetch('/data/quran_full.json')
      .then((r) => r.json())
      .then((full) => setHifzText(full[String(hifzSurah)] || []))
      .catch(() => setHifzText([]));
  }, [hifzSurah]);
  const roomSlice = useMemo(() => {
    if (hifzSurah === 1) {
      const ranges = [[0, 2], [2, 4], [4, 6], [6, 7], [0, 7]];
      return hifzText.slice(ranges[Math.min(room, 4)][0], ranges[Math.min(room, 4)][1]);
    }
    if (room >= 4) return hifzText;
    const per = Math.max(1, Math.ceil(hifzText.length / 4));
    return hifzText.slice(room * per, (room + 1) * per);
  }, [hifzText, room, hifzSurah]);

  return (
    <>
      <PageBanner
        src="/images/banners/academy_banner.png"
        eyebrow="NDIAMSYL PEYE Academy"
        title={lbl("L'Académie — 4 niveaux", 'The Academy — 4 levels', 'الأكاديمية — أربعة مستويات')}
        sub={lbl('Foriji · Digdaum · Hifz · Kang — du premier alif à la maîtrise du tafsir',
          'Foriji · Digdaum · Hifz · Kang — from the first alif to mastering tafsir',
          'الفُرْجِي · الدِّدْغَام · الحِفْظ · كَانْغ — من أول ألف إلى إتقان التفسير')}
      />
      <section id="section-academie" className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6">

      {/* Sélecteur de niveaux */}
      <div className="mt-10 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {LEVELS.map((lv) => {
          const Icon = lv.icon;
          const p = levelProgress(lv.key);
          return (
            <button
              key={lv.key}
              onClick={() => { setLevel(lv.key); stopSpeech(); setOpenModule('harakat'); setOpenRule(0); }}
              className={cn(
                'group relative overflow-hidden rounded-2xl border p-5 text-left transition-all',
                level === lv.key
                  ? 'border-accent bg-gradient-to-br from-accent/20 to-emerald-950/60 shadow-[0_0_30px_rgba(212,175,55,0.15)]'
                  : 'border-accent/20 bg-emerald-950/40 hover:border-accent/50'
              )}
            >
              <div className="flex items-center justify-between">
                <Icon className={cn('h-7 w-7', level === lv.key ? 'text-accent' : 'text-accent/60')} />
                <span className="text-xs font-bold text-accent">{p}%</span>
              </div>
              <p className="mt-3 font-display text-lg font-bold text-foreground">{lv.ar}</p>
              <p className="text-sm font-semibold text-accent/90">{uiLang === 'ar' ? lv.sub : lv[uiLang === 'en' ? 'en' : 'fr']}</p>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-black/40">
                <div className="h-full rounded-full bg-gradient-to-r from-accent to-amber-300 transition-all" style={{ width: `${p}%` }} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Intro du niveau */}
      <div className="mt-8 rounded-2xl border border-accent/20 bg-gradient-to-r from-emerald-950/60 to-black/30 p-6">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm leading-relaxed text-foreground/90">{introText}</p>
          <button
            onClick={() => speakText(introText, uiLang === 'ar' ? 'ar' : uiLang === 'en' ? 'en' : 'fr', 1)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent hover:bg-accent/30"
            title={lbl('Écouter', 'Listen', 'استمع')}
          >
            <Volume2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ================= FORIJI ================= */}
      {level === 'foriji' && (
        <div className="mt-8 space-y-6">
          {/* Modules */}
          <div className="grid gap-3 lg:grid-cols-2">
            {FORIJI_MODULES.map((mod) => {
              const open = openModule === mod.id;
              return (
                <div key={mod.id} className="rounded-xl border border-accent/20 bg-emerald-950/40">
                  <button
                    onClick={() => setOpenModule(open ? null : mod.id)}
                    className="flex w-full items-center justify-between p-4 text-left"
                  >
                    <div>
                      <p className="font-semibold text-foreground">{mod.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {lbl(`${mod.exercises} exercices`, `${mod.exercises} exercises`, `${mod.exercises} تمارين`)} · {mod.progress}%
                      </p>
                    </div>
                    {open ? <ChevronUp className="h-4 w-4 text-accent" /> : <ChevronDown className="h-4 w-4 text-accent" />}
                  </button>
                  {open && (
                    <div className="border-t border-accent/10 p-4">
                      <ul className="space-y-2.5">
                        {mod.lessons.map((lesson, i) => {
                          const key = `foriji:${mod.id}:${i}`;
                          return (
                            <li key={i} className="flex items-start gap-2.5">
                              <button onClick={() => toggleDone(key)} className="mt-0.5 shrink-0">
                                {progress[key] ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-accent/40" />}
                              </button>
                              <p className="flex-1 text-sm leading-relaxed text-foreground/85">{lesson}</p>
                              <button
                                onClick={() => speakRepeat(lesson.replace(/[()«»—ـ]/g, '').trim(), 2, 'fr', 800)}
                                className="shrink-0 rounded-full bg-accent/10 p-1.5 text-accent hover:bg-accent/25"
                                title={lbl('Écouter & Répéter', 'Listen & Repeat', 'استمع وردد')}
                              >
                                <Ear className="h-3.5 w-3.5" />
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Les 17 Makharij */}
          <div className="rounded-2xl border border-accent/25 bg-gradient-to-b from-emerald-950/70 to-black/30 p-6">
            <h3 className="font-display text-xl font-bold text-foreground">
              {lbl('Les 17 Makharij — points d\'articulation', 'The 17 Makharij — articulation points', 'مخارج الحروف السبعة عشر')}
            </h3>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {MAKHARIJ_ZONES.map((z) => (
                <button
                  key={z.zone}
                  onClick={() => speakQueue([
                    { text: z.zone, lang: 'fr', pause: 700 },
                    { text: z.letters, lang: 'ar', rate: 0.5, pause: 500 },
                    { text: z.letters, lang: 'ar', rate: 0.5 },
                  ])}
                  className="rounded-xl border p-4 text-left transition-transform hover:scale-[1.03]"
                  style={{ borderColor: `${z.color}66`, background: `linear-gradient(135deg, ${z.color}14, transparent)` }}
                >
                  <p className="text-sm font-bold" style={{ color: z.color }}>{z.zone}</p>
                  <p dir="rtl" lang="ar" className="mt-2 font-arabic text-2xl text-foreground">{z.letters}</p>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{z.detail}</p>
                  <p className="mt-3 flex items-center gap-1.5 text-xs text-accent"><Volume2 className="h-3 w-3" /> {lbl('Écouter', 'Listen', 'استمع')}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Exercice Basmala */}
          <div className="rounded-2xl border border-accent/25 bg-emerald-950/40 p-6 text-center">
            <p className="text-sm font-semibold text-accent">{lbl('Exercice final — Écouter & Répéter', 'Final exercise — Listen & Repeat', 'التمرين الأخير — استمع وردد')}</p>
            <p dir="rtl" lang="ar" className="mt-4 font-arabic text-3xl leading-loose text-foreground">بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ</p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => speakQueue([
                  { text: 'بِسْمِ', lang: 'ar', rate: 0.5, pause: 1200 },
                  { text: 'اللَّهِ', lang: 'ar', rate: 0.5, pause: 1200 },
                  { text: 'الرَّحْمَٰنِ', lang: 'ar', rate: 0.5, pause: 1200 },
                  { text: 'الرَّحِيمِ', lang: 'ar', rate: 0.5, pause: 1500 },
                  { text: 'بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ', lang: 'ar', rate: 0.6 },
                ])}
                className="flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-bold text-emerald-950 hover:bg-amber-300"
              >
                <Ear className="h-4 w-4" /> {lbl('Écouter mot à mot', 'Listen word by word', 'استمع كلمة كلمة')}
              </button>
              <button
                onClick={() => speakRepeat('بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ', 3, 'ar', 1500)}
                className="flex items-center gap-2 rounded-full border border-accent/40 px-5 py-2.5 text-sm font-medium text-accent hover:bg-accent/10"
              >
                <Volume2 className="h-4 w-4" /> {lbl('Répéter ×3', 'Repeat ×3', 'كرر ×٣')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= DIGDAUM ================= */}
      {level === 'digdaum' && (
        <div className="mt-8 space-y-3">
          {DIGDAUM_RULES.map((rule, i) => {
            const open = openRule === i;
            return (
              <div key={i} className="overflow-hidden rounded-xl border border-accent/20 bg-emerald-950/40">
                <button onClick={() => setOpenRule(open ? null : i)} className="flex w-full items-center justify-between p-4 text-left">
                  <div className="flex items-center gap-4">
                    <span dir="rtl" lang="ar" className="font-arabic text-2xl text-accent">{rule.arabicTitle}</span>
                    <div>
                      <p className="font-semibold text-foreground">{rule.title}</p>
                      {rule.letters && <p dir="rtl" lang="ar" className="text-sm text-accent/80">{rule.letters}</p>}
                    </div>
                  </div>
                  {open ? <ChevronUp className="h-4 w-4 text-accent" /> : <ChevronDown className="h-4 w-4 text-accent" />}
                </button>
                {open && (
                  <div className="border-t border-accent/10 p-5">
                    <p className="text-sm leading-relaxed text-foreground/85">{rule.explain}</p>
                    {rule.example && (
                      <div className="mt-4 flex flex-wrap items-center gap-4 rounded-lg bg-black/30 p-4">
                        <div>
                          <p dir="rtl" lang="ar" className="font-arabic text-2xl text-accent">{rule.example}</p>
                          {rule.exampleRef && <p className="mt-1 text-xs text-muted-foreground">Coran {rule.exampleRef}</p>}
                        </div>
                        <button
                          onClick={() => speakRepeat(rule.example, 2, 'ar', 1100)}
                          className="flex items-center gap-2 rounded-full bg-accent/15 px-4 py-2 text-sm text-accent hover:bg-accent/30"
                        >
                          <Ear className="h-4 w-4" /> {lbl('Écouter & Répéter', 'Listen & Repeat', 'استمع وردد')}
                        </button>
                      </div>
                    )}
                    <button onClick={() => toggleDone(`digdaum:rule:${i}`)} className="mt-4 flex items-center gap-2 text-sm text-accent hover:underline">
                      {progress[`digdaum:rule:${i}`] ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-accent/40" />}
                      {lbl('Règle maîtrisée', 'Rule mastered', 'أتقنت هذه القاعدة')}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ================= HIFZ ================= */}
      {level === 'hifz' && (
        <div className="mt-8 space-y-6">
          {/* Sélecteur sourate + salles */}
          <div className="rounded-2xl border border-accent/25 bg-gradient-to-b from-emerald-950/70 to-black/30 p-6">
            <div className="flex flex-wrap items-center gap-3">
              <BookMarked className="h-5 w-5 text-accent" />
              <select
                value={hifzSurah}
                onChange={(e) => { setHifzSurah(Number(e.target.value)); setRoom(0); }}
                className="rounded-lg border border-accent/30 bg-emerald-950 px-3 py-2 text-sm text-foreground"
              >
                {HIFZ_SURAHS.map((n) => (
                  <option key={n} value={n}>{n} — {{ 1: 'Al-Fatiha', 36: 'Ya-Sin', 55: 'Ar-Rahman', 67: 'Al-Mulk', 112: 'Al-Ikhlas', 113: 'Al-Falaq', 114: 'An-Nas' }[n]}</option>
                ))}
              </select>
              <span className="text-xs text-muted-foreground">{lbl('Palais de mémoire', 'Memory Palace', 'قصر الذاكرة')}</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {HIFZ_ROOMS.map((r, i) => (
                <button
                  key={i}
                  onClick={() => setRoom(i)}
                  className={cn('rounded-full px-4 py-1.5 text-sm transition-colors', room === i ? 'bg-accent text-emerald-950' : 'border border-accent/30 text-foreground hover:bg-accent/10')}
                >
                  {r}
                </button>
              ))}
            </div>

            {/* Versets du palais */}
            <div className="mt-5 space-y-3">
              {roomSlice.map((ayah, i) => {
                const key = `hifz:${hifzSurah}:${room}:${i}`;
                const hidden = examMode && !memorized[key];
                return (
                  <div key={i} className="relative rounded-xl border border-accent/15 bg-black/30 p-5 text-center">
                    <p dir="rtl" lang="ar" className={cn('font-arabic text-2xl leading-loose text-foreground transition-all', hidden && 'select-none blur-md')}>
                      {ayah}
                    </p>
                    {hidden && (
                      <button
                        onClick={() => setMemorized({ ...memorized, [key]: true })}
                        className="absolute inset-0 flex items-center justify-center text-sm font-medium text-accent"
                      >
                        {lbl('Cliquez si vous l\'avez récité de mémoire', 'Tap if you recited it from memory', 'اضغط إذا تلوته من الحفظ')}
                      </button>
                    )}
                    <div className="mt-3 flex justify-center gap-2">
                      <button onClick={() => speakRepeat(ayah, 2, 'ar', 1200)} className="flex items-center gap-1.5 rounded-full bg-accent/10 px-3 py-1.5 text-xs text-accent hover:bg-accent/25">
                        <Ear className="h-3 w-3" /> {lbl('Écouter ×2', 'Listen ×2', 'استمع ×٢')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Mode examen + progression */}
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <button
                onClick={() => { setExamMode(!examMode); setMemorized({}); }}
                className={cn('flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold', examMode ? 'bg-rose-500 text-white' : 'bg-accent text-emerald-950 hover:bg-amber-300')}
              >
                <GraduationCap className="h-4 w-4" />
                {examMode ? lbl('Quitter l\'examen', 'Exit exam', 'خروج من الامتحان') : lbl('Mode Examen', 'Exam Mode', 'وضع الامتحان')}
              </button>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Award className="h-4 w-4 text-accent" />
                {Object.values(memorized).filter(Boolean).length} / {roomSlice.length} {lbl('versets mémorisés', 'verses memorized', 'آية محفوظة')}
              </div>
            </div>
          </div>

          {/* 4 techniques */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {HIFZ_TECHNIQUES.map((tech) => (
              <div key={tech.title} className="rounded-xl border border-accent/20 bg-emerald-950/40 p-5">
                <p className="text-2xl">{tech.icon}</p>
                <p className="mt-2 font-semibold text-foreground">{tech.title}</p>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{tech.desc}</p>
              </div>
            ))}
          </div>

          {/* Muraja'a */}
          <div className="rounded-2xl border border-accent/25 bg-gradient-to-r from-emerald-950/60 to-black/30 p-6">
            <h3 className="flex items-center gap-2 font-display text-lg font-bold text-foreground">
              <Flame className="h-5 w-5 text-orange-400" /> {lbl('Plan de révision Muraja\'a', "Muraja'a revision plan", 'خطة المراجعة')}
            </h3>
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              {[
                { s: 'Al-Fatiha (1)', when: lbl('Urgent — aujourd\'hui', 'Urgent — today', 'عاجل — اليوم'), c: 'text-rose-400' },
                { s: 'Al-Ikhlas (112)', when: lbl('Aujourd\'hui', 'Today', 'اليوم'), c: 'text-amber-400' },
                { s: 'Al-Falaq (113) & An-Nas (114)', when: lbl('Demain', 'Tomorrow', 'غدًا'), c: 'text-emerald-400' },
              ].map((r) => (
                <button key={r.s} onClick={() => speakText(`مراجعة ${r.s}`, 'ar')} className="rounded-xl border border-accent/20 bg-black/30 p-4 text-left hover:border-accent/50">
                  <p className="text-sm font-semibold text-foreground">{r.s}</p>
                  <p className={cn('mt-1 text-xs font-medium', r.c)}>{r.when}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ================= KANG ================= */}
      {level === 'kang' && (
        <div className="mt-8 space-y-6">
          {/* Uloom */}
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {KANG_ULOOM.map((u) => (
              <button
                key={u.id}
                onClick={() => setKangUloom(u.id)}
                className={cn('rounded-xl border p-4 text-left transition-all', kangUloom === u.id ? 'border-accent bg-accent/15' : 'border-accent/20 bg-emerald-950/40 hover:border-accent/50')}
              >
                <p className="font-semibold text-foreground">{u.fr} <span dir="rtl" className="font-arabic text-accent">{u.ar}</span></p>
                <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{u.desc}</p>
              </button>
            ))}
          </div>

          {/* Les 6 exégètes */}
          <div className="rounded-2xl border border-accent/25 bg-gradient-to-b from-emerald-950/70 to-black/30 p-6">
            <h3 className="font-display text-lg font-bold text-foreground">{lbl('Les grands exégètes', 'The great exegetes', 'المفسرون الكبار')}</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {KANG_MUFASSIRUN.map((m) => (
                <div key={m.name} className="rounded-xl border border-accent/15 bg-black/30 p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-foreground">{m.name}</p>
                    <span dir="rtl" className="font-arabic text-lg text-accent">{m.ar}</span>
                  </div>
                  <p className="mt-1 text-xs text-accent/80">{m.work} · {m.era}</p>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{m.style}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Tafsir modèle : Al-Fatiha */}
          <div className="rounded-2xl border border-accent/25 bg-emerald-950/40 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-display text-lg font-bold text-foreground">
                {lbl('Tafsir modèle — Al-Fatiha', 'Model Tafsir — Al-Fatiha', 'نموذج التفسير — الفاتحة')}
              </h3>
              <select
                value={langSelect}
                onChange={(e) => setLangSelect(e.target.value as 'fr' | 'en' | 'ar')}
                className="rounded-lg border border-accent/30 bg-emerald-950 px-3 py-1.5 text-xs text-foreground"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="ar">العربية</option>
              </select>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-foreground/85">{KANG_TAFSIR_FATIHA.intro}</p>

            <h4 className="mt-5 text-sm font-bold uppercase tracking-wider text-accent">{lbl('Glossaire', 'Glossary', 'المعجم')}</h4>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {KANG_TAFSIR_FATIHA.glossary.map((g) => (
                <button
                  key={g.term}
                  onClick={() => speakQueue([{ text: g.ar, lang: 'ar', rate: 0.6, pause: 600 }, { text: g.def, lang: 'fr' }])}
                  className="rounded-lg border border-accent/15 bg-black/30 p-3 text-left hover:border-accent/50"
                >
                  <p className="text-sm font-semibold text-accent">{g.term} <span dir="rtl" className="font-arabic text-base text-foreground">{g.ar}</span></p>
                  <p className="mt-1 text-xs text-muted-foreground">{g.def}</p>
                </button>
              ))}
            </div>

            <h4 className="mt-5 text-sm font-bold uppercase tracking-wider text-accent">{lbl('Points de réflexion', 'Points of reflection', 'نقاط للتأمل')}</h4>
            <ul className="mt-3 space-y-2">
              {KANG_TAFSIR_FATIHA.reflections.map((r, i) => (
                <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-foreground/85">
                  <span className="text-accent">◆</span> {r}
                </li>
              ))}
            </ul>

            <h4 className="mt-5 text-sm font-bold uppercase tracking-wider text-accent">{lbl('Enseignements juridiques (Ahkam)', 'Legal teachings (Ahkam)', 'الأحكام')}</h4>
            <ul className="mt-3 space-y-2">
              {KANG_TAFSIR_FATIHA.ahkam.map((r, i) => (
                <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-foreground/85">
                  <span className="text-accent">⚖</span> {r}
                </li>
              ))}
            </ul>

            <h4 className="mt-5 text-sm font-bold uppercase tracking-wider text-accent">{lbl('Versets connexes', 'Related verses', 'آيات ذات صلة')}</h4>
            <div className="mt-3 space-y-2">
              {KANG_TAFSIR_FATIHA.related.map((r) => (
                <div key={r.ref} className="rounded-lg border border-accent/15 bg-black/30 p-3">
                  <p className="text-xs font-bold text-accent">Coran {r.ref}</p>
                  <p className="mt-1 text-sm text-foreground/85">{r.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </section>
    </>
  );
}
